import { isEmpty, isEqual } from 'lodash-es'
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { IEvent, IFacility, ITemplate, IWorkspace, TemplateDocs, WorkspaceBranding } from 'types'
import { useDoc } from 'utils'
import { ExplorerProviderProps } from '../../desktopTypes'
import { ExplorerCache, useExplorerCache } from '../../utils/preferences'
import { templateOverride } from '../../utils/templateOverride'

export interface IExplorerConfig {
  event?: IEvent | null
  facility?: IFacility | null
  initialized?: boolean
  baseTemplate?: ITemplate | null
  momentId?: number
  theme: Theme
  templateDocs?: TemplateDocs | null
  updateFacility: (id?: string) => void
  updateTemplateDocs: Dispatch<SetStateAction<TemplateDocs | undefined>>
  updateTemplateId: (id: string) => void
  updateWorkspace: (id?: string) => void
  workspace?: IWorkspace | null
}

export const ExplorerConfigContext = createContext<IExplorerConfig>({} as any)

export const useExplorerConfig = () => useContext(ExplorerConfigContext)

export function ExplorerConfigProvider({
  cache,
  children,
  eventId: parentEventId,
  facilityId: parentFacilityId,
  momentId,
  template,
  templateId: parentTemplateId,
}: ExplorerProviderProps) {
  const [cachedDocs, setCacheDocs] = useExplorerCache()
  const [facilityId, setFacilityId] = useState(
    (parentFacilityId ?? cache) ? cachedDocs?.facility?.id : undefined
  )
  const [templateDocs, setTemplateDocs] = useState(cache ? cachedDocs?.templateDocs : undefined)
  const [templateId, setTemplateId] = useState('')
  const event = useDoc('events', parentEventId)[0]
  const facility = useDoc('facilities', facilityId)[0]
  const baseTemplate = templateOverride || template || useDoc('templates', templateId)[0]
  const [workspaceId, setWorkspaceId] = useState(cache ? cachedDocs?.workspace?.id || '' : '')
  const [workspace] = useDoc('Workspace', workspaceId)
  const [initialized, setInitialized] = useState(false)

  if (parentEventId && momentId == null) throw new Error('Moment ID is required when using eventId')
  const getTemplateId = () =>
    parentTemplateId ||
    facility?.template?.id ||
    event?.moments.find(m => m.id === momentId)?.template?.id ||
    (cache ? cachedDocs?.templateDocs?.template?.id : undefined)

  useEffect(() => {
    if (parentFacilityId) setFacilityId(parentFacilityId)
  }, [parentFacilityId])

  useEffect(() => {
    const templateId = facility?.template?.id
    if (templateId) setTemplateId(templateId)
  }, [facility])

  useEffect(() => {
    if (initialized) return
    if (!parentEventId && !parentTemplateId && !parentFacilityId) return setInitialized(true)

    const providedTemplateId = getTemplateId()
    if (!providedTemplateId) return

    setTemplateId(providedTemplateId)
    setInitialized(true)
  }, [parentEventId, parentTemplateId, parentFacilityId, facility, event])

  useEffect(() => {
    if (workspace) {
      if (!baseTemplate) {
        const templateId = getTemplateId()
        if (templateId) setTemplateId(templateId)
        return
      }
    } else {
      if (baseTemplate) setWorkspaceId(baseTemplate.workspaceId || '')
      return
    }

    if (!cache) {
      if (!isEmpty(cachedDocs)) setCacheDocs({})
      return
    }

    const updatedCache: ExplorerCache = {
      event: event || undefined,
      facility: facility || undefined,
      templateDocs,
      workspace: workspace || undefined,
    }
    if (!isEqual(cachedDocs, updatedCache)) setCacheDocs(updatedCache)
  }, [cache, baseTemplate, facility, templateDocs, event, workspace])

  function updateWorkspace(id?: string) {
    setCacheDocs({})
    setFacilityId(undefined)
    setWorkspaceId(id ?? '')
  }

  const _templateDocs = templateDocs || cachedDocs?.templateDocs
  const _template = templateOverride || _templateDocs?.template
  const _facility = facility || cachedDocs?.facility
  const _workspace = workspace || cachedDocs?.workspace
  const _event = event || cachedDocs?.event
  const theme = useTheme(_workspace?.branding)

  return (
    <ExplorerConfigContext.Provider
      value={{
        event: _event,
        facility: _facility,
        initialized,
        theme,
        templateDocs: _templateDocs && { ..._templateDocs, template: _template! },
        baseTemplate,
        momentId,
        updateFacility: setFacilityId,
        updateTemplateDocs: setTemplateDocs,
        updateTemplateId: setTemplateId,
        updateWorkspace,
        workspace: _workspace,
      }}
    >
      {children}
    </ExplorerConfigContext.Provider>
  )
}

export interface Theme {
  color: {
    active: string
    black: string
    border: string
    danger: string
    good: string
    primary: string
    warn: string
    white: string
  }
  fontFamily: string
}

export const useTheme = (branding?: WorkspaceBranding): Theme => {
  const { fontFamily = 'Inter', color } = branding || {}

  const theme = useRef<Theme>(null)

  const updated = {
    color: {
      active: '#0099FF',
      black: '#404040',
      border: 'rgba(255, 255, 255, 0.50)',
      danger: '#FF3300',
      good: '#00cc33',
      primary: color?.primary ?? 'white',
      warn: '#FF6600',
      white: '#FFFFFF',
    },
    fontFamily,
  }

  if (!isEqual(theme.current, updated)) theme.current = updated
  return theme.current!
}
